import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import { useFeature } from "../hooks/useFeature";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { AiBotIcon } from "./AiBotIcon";
import type {
  AiChatMessage,
  AiChatRequest,
  AiChatResponse,
} from "@family/shared";
import { AI_CHAT_MAX_MESSAGES } from "@family/shared";

/**
 * Floating "Ask the family" chat bubble. Only renders for users enrolled
 * in the ai_chat beta feature. State is kept in-memory per page load —
 * no persistence by design (privacy, and to keep the conversation focused).
 */
export function ChatBot() {
  const enabled = useFeature("ai_chat");
  const meQuery = useMe();
  const slug = resolveMembership(meQuery.data)?.familySlug;

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState<AiChatMessage[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Listen for the mobile bottom-bar AI button toggle event, plus an
  // explicit "open" event used by header greeting suggestions (which
  // should always open the chat, never close it).
  useEffect(() => {
    const toggle = () => setOpen((o) => !o);
    const open = () => setOpen(true);
    window.addEventListener("ai-chat:toggle", toggle);
    window.addEventListener("ai-chat:open", open);
    return () => {
      window.removeEventListener("ai-chat:toggle", toggle);
      window.removeEventListener("ai-chat:open", open);
    };
  }, []);

  const send = useMutation<AiChatResponse, Error, AiChatRequest>({
    mutationFn: (body) =>
      apiFetch<AiChatResponse>(`/families/${slug}/ai/chat`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    },
  });

  // Opening questions built from this family's own posts, events and people.
  // Only fetched once the panel is open and the conversation is still empty.
  const suggestions = useQuery<{ suggestions: string[] }>({
    queryKey: ["ai-suggestions", slug],
    queryFn: () =>
      apiFetch<{ suggestions: string[] }>(`/families/${slug}/ai/suggestions`),
    enabled: Boolean(slug) && open && messages.length === 0,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, send.isPending]);

  if (!enabled || !slug) return null;

  function ask(text: string) {
    if (!text || send.isPending) return;
    const next: AiChatMessage[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setDraft("");
    // Keep the full history in the UI, but only send the most recent window to
    // the API so long conversations don't exceed the server's message cap.
    // Drop a leading assistant turn so the window starts with a user message
    // (the model expects user-first history).
    const windowed = next.slice(-AI_CHAT_MAX_MESSAGES);
    while (windowed.length > 1 && windowed[0]!.role !== "user") windowed.shift();
    send.mutate({ messages: windowed });
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    ask(draft.trim());
  }

  return (
    <>
      <button
        type="button"
        className="ai-chat-bubble-btn"
        aria-label={open ? "Close family AI chat" : "Open family AI chat"}
        onClick={() => setOpen((o) => !o)}
        style={{
          position: "fixed",
          bottom: 16,
          right: 16,
          width: 52,
          height: 52,
          borderRadius: "50%",
          background: "var(--c-blue, #0058a3)",
          color: "#fff",
          border: "none",
          fontSize: 22,
          boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
          cursor: "pointer",
          zIndex: 60,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
        }}
      >
        {open ? (
          <span style={{ fontSize: 26, lineHeight: 1 }}>×</span>
        ) : (
          <AiBotIcon size={28} color="#fff" />
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Family AI chat"
          style={{
            position: "fixed",
            bottom: 80,
            right: 16,
            width: "min(360px, calc(100vw - 32px))",
            height: "min(520px, calc(100vh - 120px))",
            background: "var(--c-bg, #fff)",
            color: "var(--c-text, #111)",
            border: "1px solid var(--c-border, #ddd)",
            borderRadius: 12,
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            display: "flex",
            flexDirection: "column",
            zIndex: 60,
          }}
        >
          <div
            style={{
              padding: "10px 14px",
              borderBottom: "1px solid var(--c-border, #ddd)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "var(--c-accent, #4338ca)", display: "inline-flex" }}>
                <AiBotIcon size={20} />
              </span>
              <strong>Family AI · beta</strong>
            </span>
            <span className="text-subtle" style={{ fontSize: "0.75rem" }}>
              powered by Gemini
            </span>
          </div>

          <div
            ref={scrollRef}
            style={{
              flex: 1,
              overflow: "auto",
              padding: 12,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {messages.length === 0 && (
              <div className="stack" style={{ gap: 8 }}>
                <p className="text-muted" style={{ fontSize: "0.9rem", margin: 0 }}>
                  Ask anything about the family — or start with one of these:
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {(suggestions.data?.suggestions ?? []).map((q) => (
                    <button
                      key={q}
                      type="button"
                      className="btn-secondary"
                      onClick={() => ask(q)}
                      disabled={send.isPending}
                      style={{
                        fontSize: "0.85rem",
                        minHeight: 32,
                        padding: "4px 12px",
                        borderRadius: "var(--r-pill, 999px)",
                        textAlign: "left",
                      }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <ChatBubble key={i} role={m.role} content={m.content} />
            ))}
            {send.isPending && (
              <ChatBubble role="assistant" content="…" />
            )}
            {send.error && (
              <p className="text-error" style={{ fontSize: "0.85rem" }}>
                {send.error.message}
              </p>
            )}
          </div>

          <form
            onSubmit={submit}
            style={{
              borderTop: "1px solid var(--c-border, #ddd)",
              padding: 8,
              display: "flex",
              gap: 6,
            }}
          >
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Ask about the family…"
              disabled={send.isPending}
              style={{ flex: 1, minHeight: 36 }}
              maxLength={500}
            />
            <button
              type="submit"
              disabled={!draft.trim() || send.isPending}
              style={{ minHeight: 36, padding: "0 14px" }}
            >
              Send
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function ChatBubble({ role, content }: { role: "user" | "assistant"; content: string }) {
  const mine = role === "user";
  return (
    <div
      style={{
        alignSelf: mine ? "flex-end" : "flex-start",
        maxWidth: "85%",
        background: mine ? "var(--c-accent, #4338ca)" : "var(--c-bg-alt, #f1f5f9)",
        color: mine ? "var(--c-accent-contrast, #fff)" : "var(--c-text, #111)",
        padding: "8px 12px",
        borderRadius: 12,
        fontSize: "0.9rem",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {content}
    </div>
  );
}
